import { useLoader } from '../../../contexts/LoaderContext';
import { useAlert } from '../../../contexts/AlertContext';
import { AddUserFormValues } from '../types';
import { users, RegisterUserData, UserRole } from '../../../api/users';
import { FormikHelpers } from 'formik';
import { unformatPhoneNumber } from '../../../utils/phoneMask';

interface UseAddUserFormProps {
  onSubmit: (values: AddUserFormValues) => void;
  onCancel: () => void;
}

export const useAddUserForm = ({ onSubmit, onCancel }: UseAddUserFormProps) => {
  const { showLoader, hideLoader } = useLoader();
  const { showAlert } = useAlert();

  const handleSubmit = async (
    values: AddUserFormValues,
    formikHelpers: FormikHelpers<AddUserFormValues>
  ) => {
    const { setSubmitting, validateForm, setTouched } = formikHelpers;

    setTouched({
      firstName: true,
      lastName: true,
      email: true,
      phone: true,
      agency: true,
      role: true,
      accessTo: true,
      office: true,
    });

    const errors = await validateForm();

    if (Object.keys(errors).length > 0) {
      showAlert('Please fill in all mandatory fields before submitting.', 'error');
      setSubmitting(false);
      return;
    }

    try {
      showLoader();

      const userData: RegisterUserData = {
        email: values.email,
        firstName: values.firstName,
        lastName: values.lastName,
        role: values.role as UserRole,
        agencyIds: values.agency.map((agency) => parseInt(agency)),
        isEnable: true,
        hasMarketingHubAccess: values.accessTo.includes('marketing_hub'),
        hasLeaderboardAccess: values.accessTo.includes('leaderboard'),
        ...(values.accessTo.includes('leaderboard') && { phone: unformatPhoneNumber(values.phone) }),
        ...(values.role !== UserRole.DIRECTOR &&
          values.accessTo.includes('leaderboard') &&
          values.office?.length && {
          leaderboardOfficeIds: values.office.map((id) => parseInt(id))
        }),
      };

      await users.create(userData);
      showAlert('User successfully created', 'success');
      hideLoader();
      onSubmit(values);
    } catch (error: any) {
      hideLoader();
      if (
        error.message?.toLowerCase().includes('email') ||
        error.message?.toLowerCase().includes('duplicate')
      ) {
        showAlert(
          'This email is already in use. Please enter a different email address or check existing users.',
          'error'
        );
      } else {
        showAlert(error.message || 'Failed to save user', 'error');
      }
    }

    setSubmitting(false);
  };

  return {
    handleSubmit,
    onCancel,
  };
};
